/**
 * i18n — ENGLISH IS THE KEY, the default and the fallback (`CLAUDE.md` rule 2).
 *
 * The shape is the mobile app's, re-implemented and never imported: one folder per language, one file per
 * NAMESPACE, and a lookup that falls back to English key by key. A missing translation degrades to English
 * and never to a blank — a screen that draws nothing is worse than one that draws the wrong language.
 */
/**
 * THE LANGUAGE IS MODULE STATE, and that is deliberate.
 *
 * The mobile `t(lang, key)` takes it as an argument because a React tree already threads it. Here the copy
 * is written from twelve modules that would each have to receive and forward it, and a parameter forwarded
 * through twelve hands is a parameter one of them forgets. There is exactly one language per page.
 */
/**
 * WHAT IS NOT TRANSLATED, and it is a decision (§142.4): the INSTRUMENTS. The `F1` panel, the surface
 * and grip calibration knobs and the shot log speak to whoever is building this, and they are English
 * already. Translating them is work with no reader, and it would blur the line that separates an
 * instrument from a player surface — which is the line that decides what may never be one keypress away.
 */

import en from './en/index.ts';
import es from './es/index.ts';

/** Every language the catalogue can carry. `LANGS` is what the picker offers, which is a smaller list. */
export type Lang = 'en' | 'es';

/**
 * Offered in the picker. English first because it is the default: a list whose first item is not the
 * fallback reads as if the fallback were an afterthought.
 */
export const LANGS: readonly Lang[] = ['en', 'es'];

/** What each one is called, in ITS OWN language — nobody looks for "Spanish" while reading Spanish. */
export const LANG_NAME: Record<Lang, string> = { en: 'English', es: 'Español' };

type Namespace = Record<string, string>;
const catalog: Record<Lang, Record<string, Namespace>> = { en, es };

/** Whether a string is a language this catalogue carries — the guard between a stored value and the state. */
export const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as readonly string[]).includes(v);

/**
 * THE LANGUAGE RESOLVES ON IMPORT, never when somebody remembers to call `setLang`.
 *
 * Resolving it from the boot path ties it to an ORDER, and the gate is opened by the socket's welcome —
 * which can arrive first, leaving one screen in English and the next in Spanish (§124.1). There may be no
 * moment where the right value exists and is not applied. The key is read here and not imported from
 * `prefs.ts`, which imports this module; the NAME is what must agree, and `check:i18n` compares both ends.
 */
function initial(): Lang {
  // `try` because a browser with storage blocked throws on the READ, and having no interface because a
  // language could not be remembered is worse than speaking the browser's.
  try {
    const stored = localStorage.getItem('arena.prefs.lang');
    if (isLang(stored)) return stored;
    return preferredLang();
  } catch {
    // No `localStorage` and no `navigator`: a Worker, or this module imported by a suite under Node. There
    // is no player watching, so the key — English — is the right answer and not a patch.
    return 'en';
  }
}

/**
 * The language in force. Not exported as a variable on purpose: a module that reads it directly would be
 * reading a snapshot, and the one that matters is the one at the moment the string is drawn.
 */
let current: Lang = initial();

export const lang = (): Lang => current;

/**
 * Sets it, and answers whether anything CHANGED.
 *
 * The caller uses that to decide whether to redraw: repainting the gate on a pick that selected what was
 * already selected is a flicker with no cause, and this is the only place that can tell the difference.
 */
export function setLang(next: Lang): boolean {
  if (next === current) return false;
  current = next;
  return true;
}

/**
 * What the BROWSER asks for, narrowed to what exists. The starting point when nothing was stored yet.
 *
 * Only the primary subtag is read: `es-AR`, `es-419` and `es` are one catalogue here, and treating them as
 * different keys would leave an Argentine browser on English for want of a suffix.
 *
 * THE ABSENCE OF `navigator` IS ANSWERED HERE and not by each caller. `initial` reaches this inside a
 * `try`, but `prefs.readPrefs` calls it outright — so under Node (a Worker, or a suite importing this) the
 * throw travelled up through production code that has nothing to do with a language. English is the key.
 */
export function preferredLang(): Lang {
  const asked = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language];
  for (const tag of asked) {
    const base = String(tag).toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return 'en';
}

/**
 * `t('ns.key')` — the language in force, then English, then the key itself.
 *
 * Returning the KEY rather than an empty string when both miss is what makes a missing entry visible
 * instead of invisible: `entry.title` on screen sends whoever sees it straight to the catalogue, and a
 * blank sends them nowhere (§30.2 — a gap gets investigated, a plausible value does not).
 */
export function t(path: string): string {
  const dot = path.indexOf('.');
  if (dot < 0) return path;
  const ns = path.slice(0, dot);
  const key = path.slice(dot + 1);
  return catalog[current]?.[ns]?.[key] ?? catalog.en[ns]?.[key] ?? path;
}

/**
 * PAINT THE COPY A DOCUMENT CARRIES FLAT — `<p data-i18n="ns.key">English</p>`.
 *
 * A page is on screen before its bundle lands, so the markup ships the English and names the entry that
 * replaces it beside it. Written once and shared by every page for the reason `arena.md §C` gives: two
 * appliers are two answers to which element a key covers. The attribute is also the DECLARATION
 * `check:i18n` reads, which is how a markup surface becomes something a guard can judge at all.
 */
export function applyCopy(root: ParentNode = document): void {
  // `forEach` and not `for…of`: this tsconfig carries `DOM` without `DOM.Iterable`, so a NodeList is not
  // iterable here — and widening the lib for one loop changes what every other module may assume.
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    el.textContent = t(String(el.dataset.i18n));
  });
}

/**
 * The same, with `{name}` style holes filled.
 *
 * Interpolation and not concatenation, because word order is exactly what changes between languages: an
 * English sentence built by gluing three fragments cannot be rebuilt in Spanish without re-gluing them,
 * and the module doing the gluing is the one that does not know either language.
 */
export function tf(path: string, vars: Record<string, string | number>): string {
  return t(path).replace(/\{(\w+)\}/g, (whole, k: string) => (k in vars ? String(vars[k]) : whole));
}
